import { ReactNode } from 'react';

import { FormFieldProxy, FormMessage, Label } from '@zooplus/ui-components';

import styles from '../CardNumberFormFieldNew/CardNumberFormFieldNew.module.scss';

import SupportedCardList from '@components/SupportedCardList/SupportedCardList';
import useIsMobile from '@hooks/useIsMobile';
import { useTranslation } from '@hooks/useTranslations';
import { CreditCardBrand } from '@types-internal';

interface CardNumberFormFieldProps {
  /* Id is required for rendering Adyen(data-cse)/Braintree(id) input field*/
  id?: string;
  /* The controller element to add to the end of card number input field, this item controls how the detected
   * brand icon will be displayed, and it controls the behavior of co-branded card (present list of brand, handle
   * the brand selection). */
  detectedBrandsController?: ReactNode;
  /* Indicate that the field is focused. */
  focused?: boolean;
  /* Controls whether the input label should be visible */
  icon?: React.ReactNode;
  errorMessage?: React.ReactNode;
  isBrandDetected?: boolean;
  cardBrands?: CreditCardBrand[];
  dataZta?: string;
}

/**
 * Credit card number input field for the modernize payment forms.
 *
 * Supports:
 * - Integration with Adyen/Braintree via input `id`/`data-cse`
 * - Optional brand detection and brand selection controller
 * - Display of supported card brands list, positioned differently based on device type
 */

const CardNumberFormField = ({
  id,
  detectedBrandsController,
  errorMessage,
  focused,
  icon,
  isBrandDetected,
  cardBrands = [],
  dataZta,
}: CardNumberFormFieldProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // In modernized payment flow, show card list above input for non-mobile devices (width > 544px)
  const showTopCardList = !isMobile && !isBrandDetected;
  // In modernized payment flow, show card list below input for mobile devices (width <= 544px)
  const showBottomCardList = isMobile && !isBrandDetected;

  return (
    <div>
      <div className={styles.ccNumberInputContainer}>
        <Label>{t('checkout.payment.creditcard.card_number.label')}</Label>
        {showTopCardList && <SupportedCardList cardBrands={cardBrands} size="s" />}
      </div>

      {/* FormControl should be replaced by FormFieldProxy from ui-components library as appendixItems are not supported yet it can't be changed for now */}
      <FormFieldProxy
        id={id}
        data-cse={id}
        hasError={!!errorMessage}
        hasFocus={focused}
        data-zta={dataZta ?? 'inputCreditCardNumber'}
        icon={icon}>
        {detectedBrandsController && (
          <div className={styles.appendix}>{detectedBrandsController}</div>
        )}
      </FormFieldProxy>
      {/* TODO: id is passed in relation to ARIA props need to be supported for error messages */}
      {errorMessage && (
        <FormMessage
          id="encryptedCardNumber-error-message"
          hasError={!!errorMessage}
          data-zta="errorInputCreditCardNumber">
          {errorMessage}
        </FormMessage>
      )}

      {showBottomCardList && <SupportedCardList cardBrands={cardBrands} size="s" />}
    </div>
  );
};

export default CardNumberFormField;
